/**
 * A scenario block AS WRITTEN, coloured.
 *
 * The text is the product repository's own - comments, anchors, order and
 * spacing included - and it stays that: nothing here parses the YAML or
 * writes it back. Re-rendering from parsed data would drop the comments,
 * which are the half of a scenario that says why a step or a threshold is
 * there. So this only CUTS the text into pieces and colours each one; the
 * pieces joined are the source, character for character (the tests hold
 * that), and a reader copying from the page copies what the repository says.
 *
 * It knows the YAML the corpora are written in, not all of YAML: block keys,
 * list dashes, flow [..] and {..}, quoted and plain scalars, &anchors and
 * *aliases, comments on their own line and after a value. A `|` or `>` block
 * scalar is left uncoloured below its key, so a `#` inside a shell script is
 * not mistaken for a comment. Anything it does not recognise stays plain
 * text, which is how the block read before.
 */

export type Kind =
  | "plain"
  | "comment"
  | "id"
  | "action"
  | "key"
  | "string"
  | "literal"
  | "anchor"
  | "punct";

export type Piece = { kind: Kind; text: string };

const LITERAL =
  /^(?:[-+]?(?:\d[\d_]*(?:\.\d*)?|\.\d+)(?:[eE][-+]?\d+)?|0x[0-9a-fA-F]+|[-+]?\.(?:inf|Inf|INF)|\.(?:nan|NaN|NAN)|true|True|TRUE|false|False|FALSE|yes|Yes|YES|no|No|NO|on|On|ON|off|Off|OFF|null|Null|NULL|~)$/;

// A block key at the start of what is left of a line: plain or quoted, then a
// colon that ends the line or is followed by a space.
const BLOCK_KEY = /^("(?:[^"\\]|\\.)*"|'(?:[^']|'')*'|[^\s#'"[\]{},&*!|>%@`-][^#:]*?|-[^\s#:][^#:]*?)(\s*)(:)(?=\s|$)/;

/** Cut one scalar-or-flow VALUE (what follows `key:` or `- `) into pieces. */
function value(s: string, out: Piece[]) {
  let depth = 0;
  let i = 0;
  let atTokenStart = true;
  const push = (kind: Kind, text: string) => {
    if (!text) return;
    const last = out[out.length - 1];
    if (last && last.kind === kind) last.text += text;
    else out.push({ kind, text });
  };
  while (i < s.length) {
    const c = s[i];
    if (c === " " || c === "\t") {
      let j = i;
      while (j < s.length && (s[j] === " " || s[j] === "\t")) j++;
      push("plain", s.slice(i, j));
      i = j;
      continue;
    }
    // a comment starts at a `#` that begins the value or follows a space
    if (c === "#" && (i === 0 || s[i - 1] === " " || s[i - 1] === "\t")) {
      push("comment", s.slice(i));
      return;
    }
    if (atTokenStart && c === '"') {
      let j = i + 1;
      while (j < s.length && s[j] !== '"') j += s[j] === "\\" ? 2 : 1;
      j = Math.min(j + 1, s.length);
      i = keyOrString(s, i, j, push);
      atTokenStart = false;
      continue;
    }
    if (atTokenStart && c === "'") {
      let j = i + 1;
      while (j < s.length && !(s[j] === "'" && s[j + 1] !== "'")) j += s[j] === "'" ? 2 : 1;
      j = Math.min(j + 1, s.length);
      i = keyOrString(s, i, j, push);
      atTokenStart = false;
      continue;
    }
    if (atTokenStart && (c === "[" || c === "{")) {
      depth++;
      push("punct", c);
      i++;
      continue;
    }
    if (depth > 0 && (c === "]" || c === "}" || c === ",")) {
      if (c !== ",") depth--;
      push("punct", c);
      i++;
      atTokenStart = true;
      continue;
    }
    if (atTokenStart && (c === "&" || c === "*")) {
      let j = i + 1;
      while (j < s.length && !/[\s,[\]{}]/.test(s[j])) j++;
      push("anchor", s.slice(i, j));
      i = j;
      continue;
    }
    if (atTokenStart && depth === 0 && (c === "|" || c === ">")) {
      push("punct", s.slice(i).replace(/\s+#.*$/, ""));
      const rest = s.slice(i).match(/\s+#.*$/);
      if (rest) value(rest[0], out);
      return;
    }
    // a plain scalar: to the end of the line in a block, to the next
    // separator inside [..] or {..}; a ` #` ends it either way
    let j = i;
    while (j < s.length) {
      const d = s[j];
      if ((d === "#" && (s[j - 1] === " " || s[j - 1] === "\t")) && j > i) break;
      if (depth > 0 && (d === "," || d === "]" || d === "}")) break;
      if (d === ":" && j > i && (j + 1 === s.length || /[\s,\]}]/.test(s[j + 1]))) break;
      j++;
    }
    let text = s.slice(i, j);
    const trail = text.match(/\s+$/)?.[0] ?? "";
    text = text.slice(0, text.length - trail.length);
    if (s[j] === ":" && text) {
      push("key", text);
      push("plain", trail);
      push("punct", ":");
      i = j + 1;
      atTokenStart = true;
      continue;
    }
    if (text) push(LITERAL.test(text) ? "literal" : "string", text);
    push("plain", trail);
    i = j;
    atTokenStart = false;
  }
}

/** A quoted scalar from i to j; a key when a `:` follows it. */
function keyOrString(
  s: string,
  i: number,
  j: number,
  push: (kind: Kind, text: string) => void,
): number {
  const colon = s.slice(j).match(/^(\s*):(?=\s|$|[,\]}])/);
  if (colon) {
    push("key", s.slice(i, j));
    push("plain", colon[1]);
    push("punct", ":");
    return j + colon[0].length;
  }
  push("string", s.slice(i, j));
  return j;
}

/** Cut a whole block into lines of pieces. Joined, they are the text. */
export function yamlPieces(text: string): Piece[][] {
  const lines = text.split("\n");
  const out: Piece[][] = [];
  // the indentation of a key whose value is a | or > block, while inside it
  let blockAt = -1;
  for (const line of lines) {
    const pieces: Piece[] = [];
    const indent = line.match(/^[ \t]*/)?.[0] ?? "";
    let rest = line.slice(indent.length);
    if (blockAt >= 0) {
      if (rest === "" || indent.length > blockAt) {
        if (line) pieces.push({ kind: "string", text: line });
        out.push(pieces);
        continue;
      }
      blockAt = -1;
    }
    if (indent) pieces.push({ kind: "plain", text: indent });
    if (rest.startsWith("#")) {
      pieces.push({ kind: "comment", text: rest });
      out.push(pieces);
      continue;
    }
    // list dashes, as many as the line opens with
    let column = indent.length;
    let dashed = false;
    for (let m = rest.match(/^-(\s+|$)/); m; m = rest.match(/^-(\s+|$)/)) {
      pieces.push({ kind: "punct", text: "-" });
      if (m[1]) pieces.push({ kind: "plain", text: m[1] });
      rest = rest.slice(m[0].length);
      column += m[0].length;
      dashed = true;
    }
    const key = rest.match(BLOCK_KEY);
    if (key) {
      // the scenario's id is the key at the margin; the first key of a list
      // item is what the step DOES (replay, shell, compare_golden)
      const kind: Kind = column === 0 ? "id" : dashed ? "action" : "key";
      pieces.push({ kind, text: key[1] });
      if (key[2]) pieces.push({ kind: "plain", text: key[2] });
      pieces.push({ kind: "punct", text: ":" });
      rest = rest.slice(key[0].length);
      if (/^\s+[|>][-+0-9]*\s*(#.*)?$/.test(rest)) blockAt = column;
    } else if (dashed && /^[|>][-+0-9]*\s*(#.*)?$/.test(rest)) {
      blockAt = indent.length;
    }
    value(rest, pieces);
    out.push(pieces);
  }
  return out;
}

// Only the brand's hues (tailwind.config.ts): the Tailwind scales outside it
// still exist and would paint the page off-brand. Green stays out - it means
// "passed" on this page, and a comment is not a verdict. Every pair is at
// least 4.5:1 on the surface it sits on (WCAG AA for small text), the
// punctuation included: a dash or a brace is structure, not decoration.
const TONE: Record<Kind, string> = {
  plain: "",
  comment: "italic text-slate-600 dark:text-slate-300",
  id: "font-semibold text-brand-800 dark:text-slate-100",
  action: "font-semibold text-brand-600 dark:text-sky-200",
  key: "text-sky-700 dark:text-sky-300",
  string: "text-amber-700 dark:text-amber-200",
  literal: "text-red-600 dark:text-red-300",
  anchor: "rounded bg-sky-50 text-sky-800 dark:bg-sky-950 dark:text-sky-200",
  punct: "text-slate-500 dark:text-slate-400",
};

/** The block, coloured. `className` is the <pre>'s: each place keeps its own
    box, wrapping and height, and only the text inside changes colour. */
export function YamlSource({ text, className }: { text: string; className: string }) {
  const lines = yamlPieces(text);
  return (
    <pre className={className} data-testid="yaml-source">
      {lines.map((pieces, n) => (
        <span key={n}>
          {pieces.map((p, k) =>
            p.kind === "plain" ? (
              p.text
            ) : (
              <span key={k} className={TONE[p.kind]} data-kind={p.kind}>
                {p.text}
              </span>
            ),
          )}
          {n < lines.length - 1 ? "\n" : null}
        </span>
      ))}
    </pre>
  );
}
